import { useCallback, useEffect, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { useSync } from '@/store/TournamentProvider';
import { useAuth } from '@/store/AuthProvider';
import { addMember, listMembers, removeMember, type Member } from '@/store/sync';
import { Button, Card, Input } from '@/ui';
import { confirmDialog } from '@/ui/dialogs';
import { locale, useT } from '@/i18n';
import { fehlerText, stufenHinweis } from '@/store/plan';
import { Gate, useFreigabe } from '@/components/ProMarke';

/** Einstellungen → Mitorganisatoren (docs/16 §2): per E-Mail einladen; der Eigentümer verwaltet */
export function MembersPanel() {
  const t = useT();
  const sync = useSync();
  const auth = useAuth();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [ownerEmail, setOwnerEmail] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<'owner' | 'member' | 'key' | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Limit der Stufe (docs/21): Standard 1 Mitorganisator je Turnier, Pro ohne Limit; gilt für den Eigentümer
  const einladenErlaubt = useFreigabe('mitorganisatoren', members?.length ?? 0);

  const load = useCallback(async () => {
    if (!sync.cfg) return;
    try {
      const r = await listMembers(sync.cfg);
      setMembers(r.members);
      setOwnerEmail(r.ownerEmail);
      setRole(r.role);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [sync.cfg]);
  useEffect(() => {
    void load();
  }, [load]);

  if (!sync.cfg || !auth.available) return null;

  const invite = async () => {
    if (!sync.cfg || !email.trim() || !einladenErlaubt) return;
    setBusy(true);
    try {
      setMembers(await addMember(sync.cfg, email.trim()));
      setEmail('');
      setError(null);
    } catch (e) {
      setError(t(fehlerText(e)));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (m: Member) => {
    if (!sync.cfg || !(await confirmDialog(t('{email} als Mitorganisator entfernen?', { email: m.email }), { ok: t('Entfernen'), danger: true }))) return;
    try {
      setMembers(await removeMember(sync.cfg, m.email));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const me = auth.user?.email?.toLowerCase();

  return (
    <Card
      title={t('Mitorganisatoren')}
      aside={role && <span className="label text-dim">{role === 'owner' ? t('du bist Eigentümer') : role === 'member' ? t('du bist Mitorganisator') : t('Zugriff per Helfer-Link')}</span>}
    >
      <div className="p-4 grid gap-3 text-sm">
        <p className="m-0 text-muted">
          {ownerEmail
            ? t(
                'Mitorganisatoren sehen dieses Turnier in ihrem eigenen Konto unter „In der Cloud“ und können alles bedienen — ohne Helfer-Link. Sie melden sich mit genau der eingeladenen E-Mail an. Löschen und weitere Einladungen bleiben beim Eigentümer ({email}).',
                { email: ownerEmail },
              )
            : t(
                'Mitorganisatoren sehen dieses Turnier in ihrem eigenen Konto unter „In der Cloud“ und können alles bedienen — ohne Helfer-Link. Sie melden sich mit genau der eingeladenen E-Mail an. Löschen und weitere Einladungen bleiben beim Eigentümer.',
              )}
        </p>
        {error && <div className="text-err text-xs">{error}</div>}
        {members && members.length > 0 && (
          <ul className="m-0 p-0 list-none grid gap-1">
            {members.map((m) => (
              <li key={m.email} className="flex items-center gap-3 border border-line px-3 py-1.5">
                <span className="flex-1">
                  {m.email}
                  {m.email === me && <span className="text-dim"> {t('(du)')}</span>}
                </span>
                <span className="text-xs text-dim">{new Date(m.invitedAt).toLocaleDateString(locale())}</span>
                {(role === 'owner' || m.email === me) && (
                  <button
                    type="button"
                    className="text-dim hover:text-err cursor-pointer inline-flex items-center justify-center p-1 -m-1 max-md:m-0 max-md:-my-2 max-md:min-w-11 max-md:min-h-11"
                    title={m.email === me ? t('Mich austragen') : t('Entfernen')}
                    aria-label={m.email === me ? t('Mich austragen') : t('{email} entfernen', { email: m.email })}
                    onClick={() => void remove(m)}
                  >
                    <X size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {members && members.length === 0 && <div className="text-dim text-xs">{t('Noch niemand eingeladen.')}</div>}
        {role === 'owner' && (
          <>
            {!einladenErlaubt && <div className="text-xs text-warn">{t(stufenHinweis('mitorganisatoren'))}</div>}
            <Gate feature="mitorganisatoren" anzahl={members?.length ?? 0} block>
              <form
                className="flex gap-2 flex-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  void invite();
                }}
              >
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('kollege@verein.de')} className="max-w-sm" />
                <Button type="submit" variant="primary" disabled={busy || !email.trim()}>
                  <UserPlus size={14} /> {t('Einladen')}
                </Button>
              </form>
            </Gate>
          </>
        )}
      </div>
    </Card>
  );
}
